import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import ClipLoader from "react-spinners/ClipLoader";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../All CSS/Settings.css";

const API = process.env.REACT_APP_API_BASE_URL;

function ModifyCalculation() {
  const [loading, setLoading] = useState(false);
  const [formOptions, setFormOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isGridOpen, setIsGridOpen] = useState(false);
  const [gridOptions, setGridOptions] = useState([]);
  const [selectedGrids, setSelectedGrids] = useState([]);
  const [gridSearchTerm, setGridSearchTerm] = useState("");
  const [selectedForm, setSelectedForm] = useState("");
  const [selectedDisplayValue, setSelectedDisplayValue] = useState("");
  const [hasLoadedGrid, setHasLoadedGrid] = useState(false);
  const [isModifyMode, setIsModifyMode] = useState(false);
  const [queryPopupRowId, setQueryPopupRowId] = useState(null);
  const [queryPopupField, setQueryPopupField] = useState("");
  const [queryPopupValue, setQueryPopupValue] = useState("");
  const [originalTableRows, setOriginalTableRows] = useState([]);

  const [tableRows, setTableRows] = useState([]);
  const [pageSize, setPageSize] = useState(150);
  const selectRef = useRef(null);
  const gridSelectRef = useRef(null);

  useEffect(() => {
    const loadFormOptions = async () => {
      try {
        setLoading(true);
        const res = await axios.post(
          `${API}/api/get-form-name-list-calculation`,
        );

        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        setFormOptions(rows);
      } catch (error) {
        console.error("Form list load failed", error);
        toast.error("Failed to load form list");
      } finally {
        setLoading(false);
      }
    };

    loadFormOptions();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
      if (
        gridSelectRef.current &&
        !gridSelectRef.current.contains(event.target)
      ) {
        setIsGridOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const loadGridOptions = async () => {
      if (!selectedForm) {
        setGridOptions([]);
        setSelectedGrids([]);
        return;
      }

      try {
        const res = await axios.post(
          `${API}/api/get-grd-name-list-calculation`,
          {
            FormName: selectedForm,
          },
        );

        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        setGridOptions(rows);
        setSelectedGrids([]);
        setGridSearchTerm("");
        setIsGridOpen(false);
      } catch (error) {
        console.error("Grid list load failed", error);
        setGridOptions([]);
        setSelectedGrids([]);
        toast.error("Failed to load grid list");
      }
    };

    loadGridOptions();
  }, [selectedForm]);

  const filteredOptions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) {
      return formOptions;
    }

    return formOptions.filter((option) => {
      const formName = option?.FormName || "";
      const menuName = option?.MenuName || "";

      return (
        formName.toLowerCase().includes(query) ||
        menuName.toLowerCase().includes(query)
      );
    });
  }, [formOptions, searchTerm]);

  const filteredGridOptions = useMemo(() => {
    const query = gridSearchTerm.trim().toLowerCase();

    if (!query) return gridOptions;

    return gridOptions.filter((option) =>
      String(option?.GrdName || "")
        .toLowerCase()
        .includes(query),
    );
  }, [gridOptions, gridSearchTerm]);

  const handleGridSelect = (option) => {
    const gridName = option?.GrdName || "";

    if (!gridName) {
      return;
    }

    setSelectedGrids((prev) => {
      const exists = prev.includes(gridName);
      return exists
        ? prev.filter((name) => name !== gridName)
        : [...prev, gridName];
    });

    setGridSearchTerm("");
    setHasLoadedGrid(false);
  };

  const handleSelect = (option) => {
    const formName = option.FormName || "";
    const menuName = option.MenuName || "";

    setSelectedForm(formName);
    setSelectedDisplayValue(
      menuName && formName ? `${menuName} - ${formName}` : formName,
    );
    setHasLoadedGrid(false);
    setSelectedGrids([]);
    setGridSearchTerm("");
    setIsGridOpen(false);
    setIsModifyMode(false);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleDisplay = async () => {
    if (!selectedForm) {
      toast.error("Please select a Form Name first");
      return;
    }

    const selectedGridNames = Array.from(
      new Set(selectedGrids.filter((name) => String(name).trim() !== "")),
    );

    setHasLoadedGrid(false);

    try {
      setLoading(true);

      const pendingRequests =
        selectedGridNames.length > 0
          ? selectedGridNames.map((gridName) =>
              axios.post(`${API}/api/get-calculation-grid-details`, {
                FormName: selectedForm,
                GrdName: gridName,
              }),
            )
          : [
              axios.post(`${API}/api/get-calculation-grid-details`, {
                FormName: selectedForm,
                GrdName: "",
              }),
            ];

      const responses = await Promise.all(pendingRequests);
      const allDetails = responses.flatMap((response) =>
        Array.isArray(response?.data?.data) ? response.data.data : [],
      );

      allDetails.sort((a, b) => {
        const tabOrderA = Number(a.TabOrder ?? 0);
        const tabOrderB = Number(b.TabOrder ?? 0);

        if (tabOrderA !== tabOrderB) {
          return tabOrderA - tabOrderB;
        }

        const colIndexA = Number(a.ColIndex ?? 0);
        const colIndexB = Number(b.ColIndex ?? 0);

        return colIndexA - colIndexB;
      });

      const mappedRows = allDetails.map((row, index) => ({
        ...row,
        id:
          row.id ??
          `${row.FormName ?? "row"}-${row.GrdName ?? "grid"}-${row.TabOrder ?? 0}-${row.ColIndex ?? 0}-${index}`,
      }));

      if (mappedRows.length > 0) {
        setTableRows(mappedRows);
        setOriginalTableRows(mappedRows.map((row) => ({ ...row })));
        setHasLoadedGrid(true);

        const label =
          selectedGridNames.length > 0
            ? `Displaying ${selectedForm} - ${selectedGridNames.join(", ")}`
            : `Displaying ${selectedForm}`;

        toast.success(label);
      } else {
        setTableRows([]);
        setOriginalTableRows([]);
        setHasLoadedGrid(false);

        const label =
          selectedGridNames.length > 0
            ? `No settings found for ${selectedForm} - ${selectedGridNames.join(", ")}`
            : `No settings found for ${selectedForm}`;

        toast.info(label);
      }
    } catch (error) {
      console.error("Display failed:", error);
      setTableRows([]);
      setOriginalTableRows([]);
      setHasLoadedGrid(false);
      toast.error("Unable to load selected CALCULATION settings");
    } finally {
      setLoading(false);
    }
  };

  const normalizedTableRows = useMemo(
    () =>
      tableRows.map((row, index) => ({
        ...row,
        id:
          row.id ??
          `${row.FormName ?? "row"}-${row.ObjectCode ?? "obj"}-${row.ColIndex ?? index}-${index}`,
      })),
    [tableRows],
  );

  const readOnlyFields = useMemo(() => ["FormName", "GrdName"], []);

  const updateRowField = (rowId, field, newValue) => {
    setTableRows((prevRows) =>
      prevRows.map((row) =>
        row.id === rowId ? { ...row, [field]: newValue } : row,
      ),
    );
  };

  const handleAddRow = () => {
    if (!normalizedTableRows.length) {
      return;
    }

    const sourceRow = normalizedTableRows[0] || {};

    const newRow = { ...sourceRow };

    Object.keys(newRow).forEach((field) => {
      if (field === "id") {
        newRow.id = `new-row-${Date.now()}-${Math.random()
          .toString(16)
          .slice(2)}`;
        return;
      }

      if (readOnlyFields.includes(field)) {
        newRow[field] = sourceRow[field] ?? "";
        return;
      }

      newRow[field] = "";
    });

    // Make sure new row belongs to selected form
    newRow.FormName = selectedForm;

    // New row doesn't have a ColIndex initially.
    // User must enter a unique ColIndex.
    newRow.ColIndex = "";

    setTableRows((prevRows) => [newRow, ...prevRows]);
  };

  const handleDeleteRow = (rowId) => {
    setTableRows((prevRows) => prevRows.filter((row) => row.id !== rowId));
  };

  const handleCancelEdit = () => {
    setTableRows(originalTableRows.map((row) => ({ ...row })));
    setIsModifyMode(false);
    setQueryPopupRowId(null);
    setQueryPopupField("");
    setQueryPopupValue("");
  };

  const handleOpenQueryPopup = (rowId, field, currentValue) => {
    setQueryPopupRowId(rowId);
    setQueryPopupField(field);
    setQueryPopupValue(currentValue ?? "");
  };

  const handleSaveQueryPopup = () => {
    if (
      queryPopupRowId === null ||
      queryPopupRowId === undefined ||
      !queryPopupField
    ) {
      return;
    }

    updateRowField(queryPopupRowId, queryPopupField, queryPopupValue);
    setQueryPopupRowId(null);
    setQueryPopupField("");
    setQueryPopupValue("");
  };

  const tableColumns = useMemo(() => {
    if (!normalizedTableRows.length) {
      return [];
    }

    const fields = Object.keys(normalizedTableRows[0]).filter(
      (field) => field !== "id" && field !== "ColIndex",
    );

    const columns = fields.map((field) => ({
      field,
      headerName: field
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (char) => char.toUpperCase()),
      minWidth: field.toLowerCase().includes("query") ? 220 : 120,
      flex: field.toLowerCase().includes("query") ? 1.5 : 1,
      renderCell: (params) => {
        const value = params.value;
        const isReadOnlyField = readOnlyFields.includes(field);
        const normalizedField = field.toLowerCase().replace(/[_\s-]/g, "");
        const isPopupField =
          normalizedField === "query" ||
          normalizedField === "popupquery" ||
          normalizedField === "formuladesc";
        const displayValue =
          value === null || value === undefined || value === "" ? "-" : value;

        if (!isModifyMode) {
          return (
            <span className="insert-settings-grid-cell">{displayValue}</span>
          );
        }

        if (isReadOnlyField) {
          return (
            <span className="insert-settings-grid-cell insert-settings-grid-readonly">
              {displayValue}
            </span>
          );
        }

        if (isPopupField) {
          return (
            <button
              type="button"
              className="insert-settings-grid-query-btn"
              onClick={() =>
                handleOpenQueryPopup(params.id, field, value ?? "")
              }
            >
              {value === null || value === undefined || value === ""
                ? "(empty)"
                : value}
            </button>
          );
        }

        return (
          <input
            className="insert-settings-grid-inline-input"
            value={value ?? ""}
            onChange={(event) =>
              updateRowField(params.id, field, event.target.value)
            }
          />
        );
      },
    }));

    if (isModifyMode) {
      columns.push({
        field: "delete",
        headerName: "Delete",
        minWidth: 80,
        flex: 0.4,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <button
            type="button"
            className="insert-settings-delete-btn"
            onClick={() => handleDeleteRow(params.id)}
          >
            <DeleteOutlineIcon className="insert-settings-delete-icon" />
          </button>
        ),
      });
    }

    return columns;
  }, [normalizedTableRows, isModifyMode, readOnlyFields]);

  const handleUpdate = async () => {
    if (!selectedForm) {
      toast.error("Please select a Form Name first");
      return;
    }

    if (!normalizedTableRows.length) {
      toast.error("There are no rows to update");
      return;
    }

    const invalidFormRows = normalizedTableRows.filter(
      (row) =>
        String(row?.FormName || "")
          .trim()
          .toLowerCase() !== String(selectedForm).trim().toLowerCase(),
    );

    if (invalidFormRows.length > 0) {
      toast.error("Some rows do not belong to the selected Form Name.");
      return;
    }

    const rowsWithoutGrid = normalizedTableRows.filter(
      (row) => !String(row?.GrdName || "").trim(),
    );

    if (rowsWithoutGrid.length > 0) {
      toast.error("GrdName is missing from one or more rows.");
      return;
    }

    const functionLineMap = new Map();
    const duplicateEntries = [];

    normalizedTableRows.forEach((row) => {
      const grdName = String(row?.GrdName || "").trim();
      const functionName = String(row?.FunctionName || "").trim();
      const functionLineID = row?.FunctionLineID;

      if (
        functionLineID === null ||
        functionLineID === undefined ||
        String(functionLineID).trim() === ""
      ) {
        return;
      }

      const normalizedFunctionLineID = String(functionLineID).trim();

      const key = `${grdName}|${functionName}|${normalizedFunctionLineID}`;

      if (functionLineMap.has(key)) {
        duplicateEntries.push(
          `${grdName} -> ${functionName || "(no FunctionName)"} -> FunctionLineID ${normalizedFunctionLineID}`,
        );
      } else {
        functionLineMap.set(key, true);
      }
    });

    if (duplicateEntries.length > 0) {
      const uniqueDuplicates = [...new Set(duplicateEntries)];

      toast.error(
        `Duplicate FunctionLineID found in: ${uniqueDuplicates.join(
          ", ",
        )}. Please correct it before updating.`,
      );

      return;
    }

    try {
      setLoading(true);

      const rowsToSave = normalizedTableRows.map((row) => {
        const cleanRow = { ...row };

        // DataGrid ID must not go to SQL
        delete cleanRow.id;

        // Always use selected FormName
        cleanRow.FormName = selectedForm;

        // Clean GrdName
        cleanRow.GrdName = String(cleanRow.GrdName || "").trim();

        // Clean text fields
        cleanRow.FunctionName =
          cleanRow.FunctionName == null ? "" : String(cleanRow.FunctionName);

        cleanRow.FunctionLineID =
          cleanRow.FunctionLineID == null
            ? ""
            : String(cleanRow.FunctionLineID);

        cleanRow.TargetColumn =
          cleanRow.TargetColumn == null ? "" : String(cleanRow.TargetColumn);

        cleanRow.FormulaDesc =
          cleanRow.FormulaDesc == null ? "" : String(cleanRow.FormulaDesc);

        cleanRow.TriggerColumns =
          cleanRow.TriggerColumns == null
            ? ""
            : String(cleanRow.TriggerColumns);

        cleanRow.Active = cleanRow.Active == null ? "" : cleanRow.Active;

        return cleanRow;
      });

      const res = await axios.post(
        `${API}/api/modify-calculation-grid-settings`,
        {
          FormName: selectedForm,
          Rows: rowsToSave,
        },
      );

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Failed to update GRDS Calculation.");
        return;
      }

      const rowsDeleted = Number(res?.data?.data?.rowsDeleted || 0);

      const rowsInserted = Number(res?.data?.data?.rowsInserted || 0);

      setIsModifyMode(false);

      toast.success(
        res.data.message ||
          `GRDS Calculation updated successfully. ${rowsInserted} rows inserted.`,
      );

      const selectedGridNames = Array.from(
        new Set(selectedGrids.filter((name) => String(name).trim() !== "")),
      );

      const pendingRequests =
        selectedGridNames.length > 0
          ? selectedGridNames.map((gridName) =>
              axios.post(`${API}/api/get-calculation-grid-details`, {
                FormName: selectedForm,
                GrdName: gridName,
              }),
            )
          : [
              axios.post(`${API}/api/get-calculation-grid-details`, {
                FormName: selectedForm,
                GrdName: "",
              }),
            ];

      const responses = await Promise.all(pendingRequests);

      const allDetails = responses.flatMap((response) =>
        Array.isArray(response?.data?.data) ? response.data.data : [],
      );

      allDetails.sort((a, b) => {
        const functionLineA = Number(a.FunctionLineID ?? 0);

        const functionLineB = Number(b.FunctionLineID ?? 0);

        if (functionLineA !== functionLineB) {
          return functionLineA - functionLineB;
        }

        return String(a.GrdName || "").localeCompare(String(b.GrdName || ""));
      });

      const mappedRows = allDetails.map((row, index) => ({
        ...row,
        id:
          row.id ??
          `${row.FormName ?? "row"}-${
            row.GrdName ?? "grid"
          }-${row.FunctionLineID ?? index}-${index}`,
      }));

      setTableRows(mappedRows);
      setOriginalTableRows(mappedRows.map((row) => ({ ...row })));

      setHasLoadedGrid(mappedRows.length > 0);

      console.log("GRDS Update Result:", {
        rowsDeleted,
        rowsInserted,
      });
    } catch (error) {
      console.error("GRDS Calculation update failed:", error);

      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to update GRDS Calculation settings.";

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="insert-settings-page">
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />

      <div className="insert-settings-shell">
        {loading && (
          <div className="insert-settings-loading-overlay">
            <div className="insert-settings-loading-box">
              <ClipLoader size={32} color="#2563eb" />
              <p className="insert-settings-loading-text">
                Loading, please wait...
              </p>
            </div>
          </div>
        )}

        <div className="insert-settings-header">
          <div className="insert-settings-field-wrap" ref={selectRef}>
            <div
              className={`insert-settings-select-box ${isOpen ? "open" : ""}`}
              onClick={() => setIsOpen((prev) => !prev)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setIsOpen((prev) => !prev);
                }
              }}
            >
              <div className="insert-settings-selected-value">
                {selectedDisplayValue ? (
                  <span>{selectedDisplayValue}</span>
                ) : (
                  <span className="insert-settings-placeholder">
                    Select Form Name
                  </span>
                )}
              </div>

              <button
                type="button"
                className="insert-settings-select-toggle"
                aria-label="Toggle form list"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsOpen((prev) => !prev);
                }}
              >
                <KeyboardArrowDownIcon />
              </button>
            </div>

            {isOpen && (
              <div className="insert-settings-dropdown">
                <div className="insert-settings-search-box">
                  <SearchIcon className="insert-settings-search-icon" />
                  <input
                    type="text"
                    placeholder="Search form or menu..."
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="insert-settings-search-input"
                  />
                </div>

                <div className="insert-settings-options">
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => {
                      const isSelected = option.FormName === selectedForm;

                      return (
                        <div
                          key={`${option.FormName}-${index}`}
                          className={`insert-settings-option ${isSelected ? "selected" : ""}`}
                          onClick={() => handleSelect(option)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleSelect(option);
                            }
                          }}
                        >
                          <div className="insert-settings-option-main">
                            <span className="insert-settings-option-name">
                              {option.FormName}
                            </span>
                            <span className="insert-settings-option-menu">
                              {option.MenuName}
                            </span>
                          </div>

                          {isSelected && (
                            <CheckIcon className="insert-settings-option-check" />
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="insert-settings-no-results">
                      No matching form found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div
            className="insert-settings-field-wrap insert-settings-grid-field-wrap"
            ref={gridSelectRef}
          >
            <div
              className={`insert-settings-select-box ${isGridOpen ? "open" : ""} ${!selectedForm || gridOptions.length === 0 ? "disabled" : ""}`}
              onClick={() => {
                if (selectedForm && gridOptions.length > 0) {
                  setIsGridOpen((prev) => !prev);
                }
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (
                  (event.key === "Enter" || event.key === " ") &&
                  selectedForm &&
                  gridOptions.length > 0
                ) {
                  event.preventDefault();
                  setIsGridOpen((prev) => !prev);
                }
              }}
            >
              <div className="insert-settings-selected-value">
                {selectedGrids.length > 0 ? (
                  <span>{selectedGrids.join(", ")}</span>
                ) : (
                  <span className="insert-settings-placeholder">
                    {selectedForm
                      ? "Select Grd Name"
                      : "Select Form Name first"}
                  </span>
                )}
              </div>

              <button
                type="button"
                className="insert-settings-select-toggle"
                aria-label="Toggle grid list"
                onClick={(event) => {
                  event.stopPropagation();
                  if (selectedForm && gridOptions.length > 0) {
                    setIsGridOpen((prev) => !prev);
                  }
                }}
              >
                <KeyboardArrowDownIcon />
              </button>
            </div>

            {isGridOpen && (
              <div className="insert-settings-dropdown">
                <div className="insert-settings-search-box">
                  <SearchIcon className="insert-settings-search-icon" />
                  <input
                    type="text"
                    placeholder="Search grid name..."
                    value={gridSearchTerm}
                    onChange={(event) => setGridSearchTerm(event.target.value)}
                    className="insert-settings-search-input"
                    autoFocus
                  />
                </div>

                <div className="insert-settings-options">
                  {filteredGridOptions.length > 0 ? (
                    filteredGridOptions.map((option, index) => {
                      const gridName = option?.GrdName || "";
                      const isSelected = selectedGrids.includes(gridName);

                      return (
                        <div
                          key={`${gridName}-${index}`}
                          className={`insert-settings-option ${isSelected ? "selected" : ""}`}
                          onClick={() => handleGridSelect(option)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleGridSelect(option);
                            }
                          }}
                        >
                          <div className="insert-settings-option-main">
                            <span className="insert-settings-option-name">
                              {gridName}
                            </span>
                          </div>

                          {isSelected && (
                            <CheckIcon className="insert-settings-option-check" />
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="insert-settings-no-results">
                      No matching grid found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            className="insert-settings-display-btn"
            onClick={handleDisplay}
            disabled={!selectedForm}
          >
            {hasLoadedGrid ? (
              <VisibilityIcon className="insert-settings-display-icon" />
            ) : (
              <VisibilityOffIcon className="insert-settings-display-icon" />
            )}
            Display
          </button>
        </div>

        {normalizedTableRows.length > 0 && (
          <div className="insert-settings-grid-card">
            <div className="insert-settings-grid-toolbar">
              {!isModifyMode ? (
                <button
                  type="button"
                  className="insert-settings-modify-form-btn"
                  onClick={() => setIsModifyMode(true)}
                >
                  <SystemUpdateAltIcon className="insert-settings-update-form-icon" />
                  Modify Form
                </button>
              ) : (
                <div className="insert-settings-grid-toolbar-actions">
                  <button
                    type="button"
                    className="insert-settings-new-form-btn"
                    onClick={handleAddRow}
                  >
                    <AddIcon /> Row
                  </button>

                  <button
                    type="button"
                    className="insert-settings-update-btn"
                    onClick={handleUpdate}
                    disabled={loading}
                  >
                    Update
                  </button>

                  <button
                    type="button"
                    className="insert-settings-cancel-btn"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            <div className="insert-settings-grid-wrapper">
              <DataGrid
                rows={normalizedTableRows}
                columns={tableColumns}
                getRowId={(row) => row.id}
                disableSelectionOnClick
                disableColumnFilter={false}
                rowHeight={42}
                headerHeight={32}
                pagination
                pageSize={pageSize}
                rowsPerPageOptions={[100, 150, 200, 300]}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                className="insert-settings-data-grid"
              />
            </div>
          </div>
        )}
      </div>

      {queryPopupRowId !== null && queryPopupRowId !== undefined && (
        <div
          className="insert-settings-modal-overlay"
          onClick={() => {
            setQueryPopupRowId(null);
            setQueryPopupField("");
            setQueryPopupValue("");
          }}
        >
          <div
            className="insert-settings-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="insert-settings-modal-header">
              <h3 className="insert-settings-modal-title">Edit Query</h3>
              <button
                type="button"
                className="insert-settings-modal-close"
                onClick={() => {
                  setQueryPopupRowId(null);
                  setQueryPopupField("");
                  setQueryPopupValue("");
                }}
                aria-label="Close query popup"
              >
                ×
              </button>
            </div>

            <div className="insert-settings-modal-form">
              <div className="insert-settings-modal-field">
                <label htmlFor="insert-settings-query-popup">Query</label>
                <textarea
                  id="insert-settings-query-popup"
                  value={queryPopupValue}
                  onChange={(event) => setQueryPopupValue(event.target.value)}
                  placeholder="Enter query value"
                  className="insert-settings-modal-textarea"
                  rows={6}
                />
              </div>

              <button
                type="button"
                className="insert-settings-modal-submit"
                onClick={handleSaveQueryPopup}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default ModifyCalculation;
